// Small pieces the consignment screens share: a modal shell, an item picker and a quantity box (the dialogs' lines are
// the usual table, DataTable, typed into its cells).
import { Search } from "lucide-react";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { calc } from "../../../../shared/calc.ts";
import type { Item } from "../../../../shared/db.gen.ts";
import { useRows } from "../../lib/sync.ts";
import { cx, Input, Thumb } from "../../ui/controls.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";

export function Modal({
  title,
  onClose,
  width = 760,
  children,
  footer,
}: {
  title: ReactNode;
  onClose: () => void;
  width?: number;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[84vh] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-line bg-surface shadow-pop"
        style={{ width }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="border-b border-line px-4 py-3 text-[15px] font-semibold">{title}</div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        <div className="flex items-center gap-2 border-t border-line p-3">{footer}</div>
      </div>
    </DialogLayer>
  );
}

/** Search items by SKU or name; `allow` decides which can be picked, `hint` says something about each. */
export function ItemPicker({
  allow,
  hint,
  onPick,
  placeholder = "Add an item: SKU or name…",
  className,
}: {
  allow: (item: Item) => boolean;
  hint?: (item: Item) => ReactNode;
  onPick: (item: Item) => void;
  placeholder?: string;
  className?: string;
}) {
  const items = useRows("item");
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    const exact = query.trim().toLowerCase();
    return items
      .filter((i) => !i.archived && allow(i) && words.every((w) => `${i.sku} ${i.name} ${i.barcode}`.toLowerCase().includes(w)))
      .sort((a, b) => Number(b.sku.toLowerCase() === exact) - Number(a.sku.toLowerCase() === exact) || a.sku.localeCompare(b.sku, undefined, { numeric: true }))
      .slice(0, 8);
  }, [items, query, allow]);
  const pick = (item: Item | undefined) => {
    if (!item) return;
    setQuery("");
    setCursor(0);
    onPick(item);
    input.current?.focus();
  };
  return (
    <div className={cx("relative", className)}>
      <Search className="pointer-events-none absolute top-1.5 left-2.5 size-4 text-faint" />
      <Input
        ref={input}
        value={query}
        placeholder={placeholder}
        className="h-7 pl-8 text-[12px]"
        onChange={(e) => {
          setQuery(e.target.value);
          setCursor(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") setCursor((c) => Math.min(hits.length - 1, c + 1));
          else if (e.key === "ArrowUp") setCursor((c) => Math.max(0, c - 1));
          else if (e.key === "Enter") pick(hits[cursor]);
          else return;
          e.preventDefault();
          e.stopPropagation();
        }}
      />
      {open && hits.length > 0 && (
        <div className="absolute top-8 left-0 z-50 w-[520px] max-w-[80vw] overflow-hidden rounded-lg border border-line bg-surface shadow-pop">
          {hits.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setCursor(i)}
              onClick={() => pick(item)}
              className={cx("flex w-full items-center gap-2 px-2.5 py-1.5 text-left", i === cursor && "bg-hover")}
            >
              <Thumb src={item.photo_url} size={22} />
              <span className="w-28 shrink-0 truncate font-mono text-[12px]">{item.sku}</span>
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {hint && <span className="shrink-0 font-mono text-[11px] text-muted">{hint(item)}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** A number typed in a table cell ("1,200" works); keeps what's typed and reports the number. */
export function QtyInput({
  value,
  onChange,
  className,
  ...rest
}: {
  value: string;
  onChange: (text: string) => void;
  className?: string;
  "aria-label"?: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      inputMode="decimal"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cx("h-7 w-full rounded border border-line bg-surface px-2 text-right font-mono text-[12px] focus:border-accent focus:outline-none", className)}
      {...rest}
    />
  );
}

/** A quantity typed ("1,000", or a sum: "12*24"); null when it isn't one. */
export const parseQty = (text: string): number | null => calc(text);
